// Copyright (c) Docugami, Inc. All rights reserved.

import { createEntityAdapter, createSlice, EntityState, PayloadAction } from "@reduxjs/toolkit";
import { createApi } from "@reduxjs/toolkit/dist/query/react";
import { Config } from "config";
import { AddExportConfigurationRequest, BaseExportConfigurationRequest, CloneWorkPathEntriesRequest, CloneWorkPathEntriesResponse, CreateWorkPathRequest, DocSetCollectionType, ExportConfiguration, ExportConfigurationStatus, SaveWorkPathResponse, UpdateExportConfigurationRequest, WorkPath, WorkPathEvaluationMessage, WorkPathType } from "networking/WorkPathApi";
import { Dispatch } from "react";
import type { AppState } from "store";
import { createDgBaseQuery } from "store/baseQuery";
import { TelemetryExceptions } from "telemetry/TelemetryEvents";
import { Telemetry } from "telemetry/TelemetryManager";

export const workPathsAdapter = createEntityAdapter<WorkPath>({
    selectId: x => x.id
});

export const {
    selectAll: selectAllWorkPaths
} = workPathsAdapter.getSelectors();

type WithWorkPathId<T> = T & { workPathId: string };

const projectsBaseQuery = createDgBaseQuery(Config.projectsApiUrl);
export const projectsApi = createApi({
    reducerPath: 'projectsApi',
    tagTypes: ['workPaths', 'exportConfigurations'],
    baseQuery: projectsBaseQuery,
    endpoints: builder => ({
        getWorkPaths: builder.query<EntityState<WorkPath>, void>({
            query: () => `/workpaths`,
            transformResponse: (response: WorkPath[], _, request) => {
                const knownTypes = new Set<string>(Object.values(WorkPathType));
                const workPaths = response.filter(model =>
                    model.version !== '1.0' && knownTypes.has(model.workPathType)
                );

                return workPathsAdapter.setAll(workPathsAdapter.getInitialState(), workPaths);
            },
            providesTags: ['workPaths']
        }),
        deleteWorkPaths: builder.mutation<boolean, string[]>({
            query: workPathIds => ({
                url: `/workpaths`,
                method: 'DELETE',
                body: workPathIds
            }),
            onQueryStarted: async (ids, api) => {
                await api.queryFulfilled;
                api.dispatch(projectsApi.util.invalidateTags(['workPaths']));
            }
        }),
        createWorkPath: builder.mutation<SaveWorkPathResponse, CreateWorkPathRequest>({
            query: request => {
                return {
                    url: `/workpaths`,
                    method: 'POST',
                    body: request
                }
            },
            onQueryStarted: async (arg, api) => {
                try {
                    await api.queryFulfilled;
                    api.dispatch(projectsApi.util.invalidateTags(['workPaths']));
                }
                catch (error) {
                    Telemetry.trackExceptionEvent(TelemetryExceptions.WorkPathConstructionFailed, { error: error });
                }
            }
        }),
        renameWorkPath: builder.mutation<void, WithWorkPathId<{ name: string }>>({
            query: request => ({
                url: `/workpaths/${encodeURIComponent(request.workPathId)}/name`,
                method: 'PUT',
                body: request.name
            }),
            onQueryStarted: async (arg, api) => {
                await api.queryFulfilled;
                api.dispatch(projectsApi.util.invalidateTags(['workPaths']));
            }
        }),
        cloneWorkPaths: builder.mutation<WorkPath, CloneWorkPathEntriesRequest>({
            query: request => ({
                url: `/workpaths/clone`,
                method: 'POST',
                body: request
            }),
            onQueryStarted: async (arg, api) => {
                try {
                    await api.queryFulfilled;
                    api.dispatch(projectsApi.util.invalidateTags(['workPaths']));
                }
                catch (error) {
                    Telemetry.trackExceptionEvent(TelemetryExceptions.WorkPathConstructionFailed, { error: error });
                }
            },
            transformResponse: (response: CloneWorkPathEntriesResponse) => response.workPath
        }),
    })
});

const exportBaseQuery = createDgBaseQuery(Config.exportApiUrl.replace(/\/+$/, ""));

export const workPathsApi = createApi({
    reducerPath: 'exportApi',
    tagTypes: ['exportConfigurations'],
    baseQuery: exportBaseQuery,
    endpoints: builder => ({
        getExportConfiguration: builder.query<ExportConfiguration | undefined, string>({
            query:
                workPathId => ({
                    url: `/workpaths/${workPathId}`,
                    method: 'GET'
                }),
            providesTags: (_, __, arg) => [{ type: 'exportConfigurations', id: arg }]
        }),
        addExportConfiguration: builder.mutation<void, WithWorkPathId<AddExportConfigurationRequest>>({
            query: request => ({
                url: `/workpaths/${request.workPathId}`,
                method: 'POST',
                body: request
            }),
            onQueryStarted: async (arg, api) => {
                await api.queryFulfilled;
                api.dispatch(workPathsApi.util.invalidateTags([{ type: 'exportConfigurations', id: arg.workPathId }]));
            }
        }),
        updateExportConfigurationStatus: builder.mutation<void, WithWorkPathId<UpdateExportConfigurationRequest>>({
            query: request => ({
                url: `/workpaths/${request.workPathId}/configurations/${request.configurationId}/status`,
                method: 'POST',
                body: JSON.stringify(request.disabled),
                headers: {
                    'Content-Type': 'application/json'
                }
            }),
            onQueryStarted: async (arg, api) => {
                await api.queryFulfilled;
                api.dispatch(workPathsApi.util.invalidateTags([{ type: 'exportConfigurations', id: arg.workPathId }]));
            }
        }),
        deleteExportConfiguration: builder.mutation<void, WithWorkPathId<BaseExportConfigurationRequest>>({
            query: request => ({
                url: `/workpaths/${request.workPathId}/configurations/${request.configurationId}`,
                method: 'DELETE',
                body: request
            }),
            onQueryStarted: async (arg, api) => {
                await api.queryFulfilled;
                api.dispatch(workPathsApi.util.invalidateTags([{ type: 'exportConfigurations', id: arg.workPathId }]));
            }
        }),
        isEvaluatingConfiguration: builder.query<boolean, WithWorkPathId<BaseExportConfigurationRequest>>({
            query: request => ({
                url: `/workpaths/${request.workPathId}/configurations/${request.configurationId}/evaluate`,
                method: 'GET'
            })
        }),
    })
});

export const {
    useGetExportConfigurationQuery,
    useAddExportConfigurationMutation,
    useUpdateExportConfigurationStatusMutation,
    useDeleteExportConfigurationMutation,
    useIsEvaluatingConfigurationQuery
} = workPathsApi

export const {
    useGetWorkPathsQuery,
    useDeleteWorkPathsMutation,
    useRenameWorkPathMutation,
    useCreateWorkPathMutation,
    useCloneWorkPathsMutation,
} = projectsApi

export const selectGetWorkPaths = (state: AppState) => projectsApi.endpoints.getWorkPaths.select()(state).data;


// Work path slice state

interface WorkPathModalState {
    isOpen: boolean;
    defaultType?: WorkPathType;
    defaultDocSetId?: string;
    defaultCollectionFilter?: DocSetCollectionType;
}

interface WorkPathsSliceState {
    currentWorkPath?: WorkPath;
    creationModalState: WorkPathModalState;
}

export const workPathsSlice = createSlice({
    name: 'workPaths',
    initialState: {
        creationModalState: {
            isOpen: false
        }
    } as WorkPathsSliceState,
    reducers: {
        setCurrentWorkPath(state, action: PayloadAction<WorkPath | undefined>) {
            state.currentWorkPath = !!action.payload ? {
                ...action.payload,
                resources: { ...action.payload.resources },
            } : undefined;
        },
        openWorkPathModal: (state, action: PayloadAction<{
            type?: WorkPathType;
            docSetId?: string;
            collectionFilter?: DocSetCollectionType;
        }>) => {
            state.creationModalState.isOpen = true;
            state.creationModalState.defaultType = action.payload.type;
            state.creationModalState.defaultDocSetId = action.payload.docSetId;
            state.creationModalState.defaultCollectionFilter = action.payload.collectionFilter;
        },
        closeWorkPathModal: (state) => {
            state.creationModalState.isOpen = false;
            state.creationModalState.defaultType = undefined;
            state.creationModalState.defaultDocSetId = undefined;
            state.creationModalState.defaultCollectionFilter = undefined;
        }
    }
});

export const { setCurrentWorkPath, openWorkPathModal, closeWorkPathModal } = workPathsSlice.actions;

export function handleWorkPathExportInitialization(message: WorkPathEvaluationMessage): any {
    return function (dispatch: Dispatch<any>, getState: (() => AppState)) {
        if (message.succeeded === undefined) {
            return;
        }

        dispatch(workPathsApi.util.updateQueryData('getExportConfiguration', message.workPathId, config => {
            if (!!config) {
                config.status = message.succeeded ? ExportConfigurationStatus.Ready : ExportConfigurationStatus.Uninitialized;
            }
        }));
    }
}
